import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'plum'
  | 'green'
  | 'outline'
  | 'ghost'
  | 'soft'
  | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'relative inline-flex items-center justify-center gap-[7px] border font-semibold ' +
  'leading-none whitespace-nowrap cursor-pointer select-none ' +
  'transition-[transform,box-shadow,background-color,border-color,color] ' +
  'duration-[var(--duration-base)] ease-[var(--ease-brand)] ' +
  'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 ' +
  'disabled:active:translate-y-0';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-plum-700 text-white hover:bg-plum-800 shadow-[var(--shadow-xs)] hover:shadow-[var(--shadow-sm)]',
  plum: 'border-transparent bg-plum-700 text-white hover:bg-plum-800 shadow-[var(--shadow-xs)]',
  green:
    'border-transparent bg-green-700 text-white hover:bg-green-800 shadow-[var(--shadow-xs)]',
  outline:
    'border-ink-200 bg-white text-ink-800 hover:bg-ink-50 hover:border-ink-300',
  ghost: 'border-transparent bg-transparent text-ink-700 hover:bg-ink-100',
  soft: 'border-plum-200 bg-plum-50 text-plum-800 hover:bg-plum-100',
  danger: 'border-transparent bg-red-500 text-white hover:bg-red-600',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-[34px] px-3 text-[12.5px] rounded-[var(--radius-sm)] gap-[5px]',
  md: 'h-[42px] px-4 text-sm rounded-[var(--radius-md)]',
  lg: 'h-[50px] px-5 text-[15px] rounded-[var(--radius-lg)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
  busy?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  icon,
  busy = false,
  className = '',
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    BASE,
    VARIANTS[variant],
    SIZES[size],
    block ? 'flex w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button

      type={type}
      className={classes}
      disabled={disabled ?? busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
